import { useEffect, useState } from 'react';
import { WatchlistApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { formatDateYearMonth, handleAdminError, addressTooltipText } from '../../common/SharedFunctions';
import DataGrid, { Column, Selection } from 'devextreme-react/data-grid';
import LoadPanel from 'devextreme-react/load-panel';
import { Tooltip } from 'devextreme-react/tooltip';

const EntityLoansTable = ({ watchlistId, entityId }) => {
    if (!entityId) {
        return null;
    }
    const [entity, set_entity] = useState(null);
    const [watchlistItems, set_watchlistItems] = useState([]);
    const [itemId, set_itemId] = useState('');
    const [loanId, set_loanId] = useState('');
    const [propertyId, set_propertyId] = useState('');
    const [bfiNumber, set_bfiNumber] = useState('');
    const [notes, set_notes] = useState([]);
    const [loadingMessage, set_loadingMessage] = useState('');
    const watchlistApi = new WatchlistApi.WatchlistApi(applicationInsights);

    const styles = {
        sectionDirection: {
            fontSize: '12px',
            color: 'black',
            fontStyle: 'italic',
        },
        watchlistItemNoteSection: {
            border: '1px solid lightgray',
            borderRadius: '5px',
            background: 'whitesmoke',
            padding: '10px',
            margin: '10px',
        },
        watchlistItemNoteHeader: {
            fontWeight: 'lighter',
            textDecoration: 'underline',
            paddingBottom: '5px',
        },
    };

    const loadWatchlistEntity = async (entityId) => {
        try {
            set_loadingMessage('Retrieving entity...');
            const response = await watchlistApi.getEntitySummaryById(entityId);
            if (response.statusCode !== 200) {
                throw new Error('Failed to retrieve entity data!');
            }
            const entity = response.result;
            set_entity(entity);
        } catch (ex) {
            handleAdminError(ex, 'Failed to retrieve entity record! Please refresh the page and try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    const loadWatchlistItems = async (watchlistId, entityId) => {
        try {
            set_loadingMessage('Retrieving watchlist items...');
            const response = await watchlistApi.getEntityWatchlistItems(watchlistId, entityId);
            if (response.statusCode !== 200) {
                throw new Error('Failed to retrieve item data!');
            }
            const records = response.result;
            set_watchlistItems(records);
            if (records.length > 0) {
                var item = records[0];
                set_itemId(item.id);
                set_loanId(item.loanId);
                set_propertyId(item.propertyId);
                set_bfiNumber(item.bfiNumber);
            }
        } catch (ex) {
            handleAdminError(ex, 'Failed to retrieve item records! Please refresh the page and try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    const loadNotes = async (loanId, propertyId) => {
        try {
            set_loadingMessage('Retrieving Watchlist Item Notes...');
            if (propertyId) {
                const response = await watchlistApi.getPropertyNotes(propertyId);
                if (response.statusCode !== 200) {
                    throw new Error('Failed to retrieve Property note data!');
                }
                const records = response.result;
                set_notes(records);
            } else if (loanId) {
                const response = await watchlistApi.getLoanNotes(loanId);
                if (response.statusCode !== 200) {
                    throw new Error('Failed to retrieve Loan note data!');
                }
                const records = response.result;
                set_notes(records);
            } else {
                throw new Error('Watchlist item notes not found - no item information provided.');
            }
        } catch (ex) {
            handleAdminError(ex, 'Failed to retrieve note records! Please refresh the page and try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    useEffect(() => {
        if (!watchlistId || !entityId) {
            return;
        }
        loadWatchlistEntity(entityId);
        loadWatchlistItems(watchlistId, entityId);
    }, [entityId, watchlistId]);

    useEffect(() => {
        if (!loanId && !propertyId) {
            return;
        }

        loadNotes(loanId, propertyId);
    }, [loanId, propertyId]);

    const onDateRender = (e) => {
        if (!e.value) {
            return null;
        }
        return formatDateYearMonth(e.value);
    };

    const onBFINumberRender = (e) => {
        const targetId = `wi_${e.data.id}`;
        const text = addressTooltipText(e.data.propertyAddress);
        return (
            <>
                <div id={targetId}>{e.value}</div>
                {e.data.propertyId && (
                    <Tooltip
                        target={'#' + targetId}
                        showEvent='mouseenter'
                        hideEvent='mouseleave'
                        hideOnOutsideClick={false}
                    >
                        {text}
                    </Tooltip>
                )}
            </>
        );
    };

    const onRowSelect = (e) => {
        if (e.currentSelectedRowKeys.length !== 1) {
            return;
        }
        const newItemId = e.currentSelectedRowKeys[0];
        const newLoanId = e.selectedRowsData[0].loanId;
        const newPropertyId = e.selectedRowsData[0].propertyId;
        if (itemId != newItemId) {
            set_itemId(newItemId);
            set_loanId(newLoanId);
            set_propertyId(newPropertyId);
            set_bfiNumber(e.selectedRowsData[0].bfiNumber);
        }
    };

    if (!watchlistItems) {
        return;
    }

    return (
        <>
            <div>
                <span style={{ fontWeight: 'bold' }}>Borrower:</span> <br />
                {entity?.entityName}
            </div>
            <br />
            <h2 className='decorated'>Entity Notes</h2>
            <DataGrid
                dataSource={entity?.entityNotes}
                rowAlternationEnabled={false}
                showBorders={true}
                showColumnHeaders={true}
                showRowLines={true}
                wordWrapEnabled={true}
                noDataText={'No Notes found for this entity'}
                loadPanel={true}
                onSelectionChanged={onRowSelect}
                selectedRowKeys={[entity?.entityNotes?.startDate]}
            >
                <Column
                    dataField='startDate'
                    caption='Month'
                    cellRender={onDateRender}
                    width='70'
                    sortIndex={0}
                    sortOrder={'desc'}
                />
                <Column
                    dataField='notes'
                    caption='Notes'
                    wordWrapEnabled={true}
                />
            </DataGrid>
            <br />
            <h2 className='decorated'>Watchlist Items</h2>
            <div style={styles.sectionDirection}>Click on any row to view Notes for that loan...</div>
            <DataGrid
                keyExpr={'id'}
                dataSource={watchlistItems}
                rowAlternationEnabled={false}
                showBorders={true}
                showColumnHeaders={true}
                showRowLines={true}
                wordWrapEnabled={true}
                noDataText={'No Watchlist Items found for this Entity'}
                loadPanel={true}
                onSelectionChanged={onRowSelect}
                selectedRowKeys={[itemId]}
            >
                <Column
                    dataField='bfiNumber'
                    caption='BFI #'
                    width='90'
                    sortIndex={0}
                    sortOrder='asc'
                    cellRender={onBFINumberRender}
                />
                <Column
                    dataField='issue'
                    caption='Issue(s)'
                    alignment='left'
                    width='150'
                />
                <Column
                    dataField='actionPlan'
                    caption='Action Plan'
                    alignment='left'
                />
                <Selection mode='single' />
            </DataGrid>
            <br />
            <div style={styles.watchlistItemNoteSection}>
                <div style={styles.watchlistItemNoteHeader}>Notes: {bfiNumber}</div>
                <div style={styles.sectionDirection}>Hover over cell to see full notes if they are truncated</div>
                <DataGrid
                    keyExpr={'startDate'}
                    dataSource={notes}
                    rowAlternationEnabled={true}
                    showBorders={true}
                    showColumnHeaders={true}
                    showRowLines={true}
                    wordWrapEnabled={true}
                    noDataText={'No Notes found for this Watchlist Item'}
                    height={200}
                >
                    <Column
                        dataField='startDate'
                        caption='Month'
                        cellRender={onDateRender}
                        width='70'
                        sortIndex={0}
                        sortOrder={'desc'}
                    />
                    <Column
                        dataField='notes'
                        caption='Notes'
                        wordWrapEnabled={true}
                    />
                </DataGrid>
                <LoadPanel
                    hideOnOutsideClick={false}
                    visible={!!loadingMessage}
                    message={loadingMessage}
                />
            </div>
        </>
    );
};

export default EntityLoansTable;
